import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { IAuthInfo, ILoginState } from '../login/store/state';
import { getUserInfo } from '../login/store/selectors';
import { getRelaxedUiAuth } from '../authorizations/selector';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  guard$: Observable<IAuthInfo> = this.lux.get(getUserInfo);
  relaxedAuth = toSignal(this.lux.get(getRelaxedUiAuth), { initialValue: {} as IUiAuthorizations | {} });
  userInfo = JSON.parse(localStorage.getItem('login-state') || 'null') as IAuthInfo | null;
  isLoggedIn = false;

  constructor(
    private readonly router: Router,
    private readonly lux: Lux<{ login: ILoginState, appUiAuth: IAppAuthorizations }>
  ) {}

  authGuardRouteMapping: Record<string, string> = {
    '/eligibility/member': 'manageeligibility',
    '/eligibility/add-member': 'manageeligibility',
    '/eligibility/family-enrollment': 'manageeligibility',
    '/eligibility/mass-terminate': 'manageeligibility',
    '/eligibility/mass-reinstate': 'manageeligibility',
    '/eligibility/mass-transfer': 'manageeligibility',
    '/eligibility/member-merge': 'manageeligibility',
    '/details': 'manageeligibility',
    '/add-member': 'manageeligibility',
    '/family-enrollment': 'manageeligibility',
    '/benefits': 'benefitsInquiry',
    '/reports': 'reports',
    '/billing': 'billing',
    '/summary-of-changes': 'summaryOfChanges',
    '/clientknowledge': 'clientknowledge',
    '/dentist': 'dentistDirectory',
    '/sat': 'sat',
    '/userAdmin/create': 'userAdmin',
    '/userAdmin/manage': 'userAdmin',
    '/manage-user': 'userAdmin',
    '/profile/authorizations': 'profile',
    '/profile/updatePassword': 'profile',
    '/profile/mfa': 'profile',
    '/help': 'profile',
    '/profile': 'profile'
  };

  canActivate(
    next: ActivatedRouteSnapshot,
    snapshot: RouterStateSnapshot
  ): Observable<boolean> {
    return this.guard$.pipe(
      first(),
      map(info => {
        const resolvedInfo = info || this.userInfo;
        const leftNavFeatures = ['profile', ...(((this.relaxedAuth() as IUiAuthorizations)?.leftnav) || [])];
        this.isLoggedIn = this.isSessionActive(resolvedInfo);
        const requiredFeature = this.authGuardRouteMapping[snapshot.url];
        return this.isLoggedIn && (requiredFeature ? leftNavFeatures.includes(requiredFeature) : false);
      }),
      tap(allowed => {
        if (!allowed) {
          if (!this.isLoggedIn) {
            void this.router.navigate(['/login']);
          } else {
            void this.router.navigate(['/notAuthorized']);
          }
        }
      })
    );
  }

  private isSessionActive(info: IAuthInfo | null): boolean {
    if (!info) {
      return false;
    }

    const expirationTime = info.expirationTime
      ?? ((info.tokenValidDuration ?? 0) && (info.additionalInfo?.auth_time ?? (info as IAuthInfo & { auth_time?: number }).auth_time)
        ? info.tokenValidDuration + (info.additionalInfo?.auth_time ?? (info as IAuthInfo & { auth_time?: number }).auth_time ?? 0)
        : 0);

    return !!expirationTime && (new Date().valueOf() / 1000) < expirationTime;
  }
}
